import {
  type MenuResponse,
  type QueryParamsType,
  type SiteResponse,
  SiteTemplate,
  v1MenusDetail,
  v1SitesMenusDetail,
} from '@on3/api';
import { type NextApiParams } from '@on3/ui-lib/types/next';
import { proxyApi } from '@on3/ui-lib/utils/api';
import { type GetServerSidePropsContext } from 'next';

export enum HeaderThemes {
  BASE = 'base',
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  RIVALS = 'rivals',
  BRAND = 'brand',
}

export enum SiteThemes {
  ON3 = 'on3',
  RIVALS = 'rivals',
  SITE = 'site', // Team, Pro, Misc
}

export const getSiteChecks = ({
  site,
  pathname,
  query,
}: {
  site?: SiteResponse;
  pathname: string;
  query: QueryParamsType;
}) => {
  const isOn3 = site?.key === 44;
  const isKsr = site?.key === 395 || site?.key === 24;
  const isHer = site?.key === 400;
  const isHighSchool = site?.key === 402;
  const isNil = site?.key === 368;
  const isPro = site?.key === 397;
  const isSoftballAmerica = site?.key === 401;
  const isTransferPortal = site?.key === 369;
  const isChannel = (site?.isChannel ?? false) && !isNil && !isTransferPortal;
  const isTeam = !!site?.isTeam && site?.template !== SiteTemplate.Rivals;
  const isPlayer = pathname.startsWith('/rivals/[id]');
  const isCoach = pathname.includes('/rivals/coach');
  const isSearch = pathname.includes('/rivals/search');
  const isNascar = isPro && query.category === 'nascar';
  const isLogin = pathname.includes('/login');
  const isAuth = [
    '/login',
    '/register',
    '/reset-password',
    '/forgotpassword',
  ].some((path) => pathname.includes(path));
  const isRecruits = site?.key === 409;
  const isRivalsHome = site?.key === 411;
  const isRivals = pathname.startsWith('/rivals');
  const isRivalsTeam = !!site?.isTeam && site?.template === SiteTemplate.Rivals;
  const isHighSchoolTeam = site?.template === SiteTemplate.HighSchool;
  const isBet = site?.key === 489;

  return {
    isOn3,
    isKsr,
    isHer,
    isHighSchool,
    isNil,
    isPro,
    isSoftballAmerica,
    isTransferPortal,
    isChannel,
    isTeam,
    isPlayer,
    isCoach,
    isSearch,
    isNascar,
    isRivalsHome,
    isLogin,
    isAuth,
    isRecruits,
    isRivals,
    isRivalsTeam,
    isHighSchoolTeam,
    isBet,
  };
};

export const getSiteMenus = (
  site: SiteResponse,
  ctx: NextApiParams & GetServerSidePropsContext,
): ((() => Promise<MenuResponse[]>) | null)[] => {
  const on3Key = 44;
  const on3MenuKey = 90;
  const rivalsMenuKey = 582125;
  const trendingMenuKey = 605007;
  const rivalsTrendingMenuKey = 560558;

  const siteChecks = getSiteChecks({
    site,
    pathname: ctx.resolvedUrl ?? ctx.pathname ?? site.url ?? '',
    query: ctx.query ?? {},
  });

  const primaryMultiMenuKey =
    getSiteTheme(siteChecks) === SiteThemes.RIVALS ? rivalsMenuKey : on3MenuKey;

  const siteTrendingMenuKey =
    getSiteTheme(siteChecks) === SiteThemes.RIVALS
      ? rivalsTrendingMenuKey
      : trendingMenuKey;

  const defaultMenus = [() => v1SitesMenusDetail(proxyApi, on3Key), null];

  const primarySiteMenuOnly = [
    () => v1SitesMenusDetail(proxyApi, site.key),
    null,
  ];

  const multiSiteMenu = [
    () => v1MenusDetail(proxyApi, primaryMultiMenuKey),
    () => v1SitesMenusDetail(proxyApi, site.key),
  ];

  const multiSiteTrendingMenu = [
    () => v1SitesMenusDetail(proxyApi, site.key),
    () =>
      v1MenusDetail(proxyApi, siteTrendingMenuKey).then((res) => {
        res.unshift({
          key: 0,
          id: 0,
          parent: 0,
          title: 'Message Boards',
          url: `${site.primaryForum ? site.primaryForum.url : '/boards/'}`,
        });

        return res.slice(0, 5);
      }),
  ];

  const secondarySiteTrendingMenu = [
    null,
    () => v1SitesMenusDetail(proxyApi, site.key),
    !siteChecks.isAuth
      ? () =>
          v1MenusDetail(proxyApi, siteTrendingMenuKey).then((res) => {
            if (!siteChecks.isRivalsTeam) return res.slice(0, 5);

            res.unshift({
              key: 0,
              id: 0,
              parent: 0,
              title: 'Message Boards',
              url: site.primaryForum ? site.primaryForum.url : '/boards/',
            });

            return res.slice(0, 5);
          })
      : null,
  ];

  // Multi-site menus (on3, site-specific)
  if (
    siteChecks.isRecruits ||
    siteChecks.isChannel ||
    siteChecks.isTransferPortal ||
    siteChecks.isHer ||
    siteChecks.isHighSchool ||
    siteChecks.isNil ||
    siteChecks.isHighSchoolTeam ||
    siteChecks.isBet
  ) {
    return multiSiteMenu;
  }

  // Site-specific menu only
  if (siteChecks.isKsr) {
    return primarySiteMenuOnly;
  }

  if (siteChecks.isTeam || siteChecks.isPro) {
    return multiSiteTrendingMenu;
  }

  // Site-specific menu for secondary
  if (siteChecks.isOn3 || siteChecks.isRivalsHome || siteChecks.isRivalsTeam) {
    return secondarySiteTrendingMenu;
  }

  return defaultMenus;
};

export const getHeaderTheme = (
  siteChecks: ReturnType<typeof getSiteChecks>,
) => {
  if (siteChecks.isOn3 || siteChecks.isRivals) return HeaderThemes.PRIMARY;

  if (siteChecks.isPro || siteChecks.isSoftballAmerica || siteChecks.isTeam)
    return HeaderThemes.BRAND;

  if (siteChecks.isRivalsTeam) return HeaderThemes.RIVALS;

  return HeaderThemes.SECONDARY;
};

export const getSiteTheme = (
  siteChecks: ReturnType<typeof getSiteChecks>,
): SiteThemes => {
  if (
    siteChecks.isRecruits ||
    siteChecks.isRivals ||
    siteChecks.isPlayer ||
    siteChecks.isHighSchool ||
    siteChecks.isHighSchoolTeam
  )
    return SiteThemes.RIVALS;

  if (
    siteChecks.isChannel ||
    siteChecks.isTeam ||
    siteChecks.isRivalsTeam ||
    siteChecks.isPro
  )
    return SiteThemes.SITE;

  return SiteThemes.ON3;
};
